import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { demoMode, apiRoot, commentSection } from '../../config/config';

export type Comment = {
    id: number;
    timestamp: string;
    header: string;
    text: string;
};

export const useComments = () => {
    return useQuery<Comment[]>({
        queryKey: ['comments'],
        queryFn: fetchComments,
        enabled: commentSection,
    });
};

export const useCreateComment = () => {
    const queryClient = useQueryClient();

    return useMutation<Comment, Error, Omit<Comment, 'id' | 'timestamp'>>({
        mutationFn: createComment,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comments'] });
        },
        mutationKey: commentSection ? ['createComment'] : undefined,
    });
};

export const useDeleteComment = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: deleteComment,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comments'] });
        },
        mutationKey: commentSection ? ['deleteComment'] : undefined,
    });
};

const fetchComments = async (): Promise<Comment[]> => {
    if (!commentSection) {
        return [];
    }

    if (demoMode) {
        return generateDummyComments();
    }

    const response = await axios.get(`${apiRoot}comments`);
    return response.data;
};

const createComment = async (commentData: Omit<Comment, 'id' | 'timestamp'>): Promise<Comment> => {
    if (!commentSection) {
        throw new Error('Comment section is disabled');
    }

    if (demoMode) {
        return {
            ...commentData,
            id: Math.random(),
            timestamp: new Date().toISOString()
        };
    }

    const response = await axios.post(`${apiRoot}comments`, commentData);
    return response.data;
};

const deleteComment = async (id: string): Promise<void> => {
    if (!commentSection) {
        throw new Error('Comment section is disabled');
    }

    if (demoMode) {
        return;
    }

    await axios.delete(`${apiRoot}comments/${id}`);
};

const generateDummyComments = (): Comment[] => {
    return [
        {
            id: 1,
            timestamp: new Date().toISOString(),
            header: "Demo Comment 1",
            text: "This is a sample comment"
        },
        {
            id: 2,
            timestamp: new Date().toISOString(),
            header: "Demo Comment 2",
            text: "Another sample comment"
        }
    ];
};